import jsPDF from "jspdf";
import { autoTable } from "jspdf-autotable";
import { format } from "date-fns";

/** What both the owner's and the superadmin's payment rows carry. */
export interface InvoicePayment {
  invoiceNumber: number;
  planName: string;
  durationDays: number;
  amount: number;
  currency: string;
  status: "PENDING" | "PAID" | "FAILED";
  periodStartsAt: string | null;
  periodEndsAt: string | null;
  paidAt: string | null;
  createdAt: string;
}

// The company that sells the subscription, on every invoice from either build.
const SELLER = "Softwarezz";

const INK: [number, number, number] = [17, 24, 39];
const MUTED: [number, number, number] = [96, 105, 117];
const LINE: [number, number, number] = [225, 228, 232];
const PAID_GREEN: [number, number, number] = [23, 102, 63];

const day = (value: string | null) => (value ? format(new Date(value), "d MMM yyyy") : "—");
const money = (amount: number, currency: string) =>
  `${amount.toLocaleString("en-US", { minimumFractionDigits: currency === "LBP" ? 0 : 2, maximumFractionDigits: 2 })} ${currency}`;

/**
 * Downloads the invoice for one subscription payment as a PDF. Drawn in the
 * browser from the payment row, so it always matches what the screen shows.
 */
export const downloadSubscriptionInvoice = (payment: InvoicePayment, customerName: string) => {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const left = 20;
  const right = 190;

  // Header
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(...INK);
  doc.text(SELLER, left, 26);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...MUTED);
  doc.text("Booking platform subscription", left, 32);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.setTextColor(...INK);
  doc.text("INVOICE", right, 26, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...MUTED);
  doc.text(`#${payment.invoiceNumber}`, right, 32, { align: "right" });

  doc.setDrawColor(...LINE);
  doc.line(left, 40, right, 40);

  // Bill to / details
  doc.setFontSize(9);
  doc.setTextColor(...MUTED);
  doc.text("BILLED TO", left, 50);
  doc.text("DETAILS", 120, 50);
  doc.setFontSize(11);
  doc.setTextColor(...INK);
  doc.setFont("helvetica", "bold");
  doc.text(doc.splitTextToSize(customerName, 90), left, 57);
  doc.setFont("helvetica", "normal");

  const details: [string, string][] = [
    ["Issued", day(payment.createdAt)],
    ["Paid", payment.status === "PAID" ? day(payment.paidAt) : "Not paid"],
    ["Method", "Whish"],
  ];
  details.forEach(([label, value], i) => {
    const y = 57 + i * 6;
    doc.setTextColor(...MUTED);
    doc.text(label, 120, y);
    doc.setTextColor(...INK);
    doc.text(value, right, y, { align: "right" });
  });

  // Line item
  const period =
    payment.periodStartsAt && payment.periodEndsAt
      ? `${day(payment.periodStartsAt)} – ${day(payment.periodEndsAt)}`
      : `${payment.durationDays} days`;
  autoTable(doc, {
    startY: 82,
    margin: { left, right: 210 - right },
    head: [["Description", "Period", "Amount"]],
    body: [[`${payment.planName} package`, period, money(payment.amount, payment.currency)]],
    theme: "plain",
    styles: { font: "helvetica", fontSize: 10, textColor: INK, cellPadding: { top: 4, bottom: 4, left: 2, right: 2 } },
    headStyles: { textColor: MUTED, fontStyle: "bold", fontSize: 9, lineColor: LINE, lineWidth: { bottom: 0.3 } },
    bodyStyles: { lineColor: LINE, lineWidth: { bottom: 0.3 } },
    columnStyles: { 2: { halign: "right" } },
    // columnStyles skips the header row; the Amount heading lines up too.
    didParseCell: (data) => {
      if (data.column.index === 2) data.cell.styles.halign = "right";
    },
  });

  const afterTable = (doc as any).lastAutoTable?.finalY ?? 110;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("Total", 120, afterTable + 12);
  doc.text(money(payment.amount, payment.currency), right, afterTable + 12, { align: "right" });

  // Status stamp
  doc.setFontSize(11);
  if (payment.status === "PAID") {
    doc.setTextColor(...PAID_GREEN);
    doc.text("PAID", left, afterTable + 12);
  } else {
    doc.setTextColor(163, 43, 34);
    doc.text(payment.status === "FAILED" ? "NOT PAID" : "PENDING", left, afterTable + 12);
  }

  // Footer
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...MUTED);
  doc.text(
    "Payments are made online with Whish. Subscriptions do not renew automatically.",
    left,
    280
  );

  doc.save(`invoice-${payment.invoiceNumber}.pdf`);
};
